import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { IPaginationPayload } from '../../interfaces/shared/IPaginationPayload';
import { tryToChangeTeamMembersStatus, tryToDeleteTeamMembers, tryToFetchAllTeamMembers, tryToFetchTeamMembers, tryToUpdateTeamMemberRole } from '../../store/brokers/partners/teams/teamsSlice';
import Button from '../../shared/button';
import Error from '../../shared/error';
import Input from '../../shared/input';
import SelectCheckbox from '../../shared/select-checkbox';
import { MEMBER_STATUS } from '../../utils/constants/member-status';
import { useTheme } from '../../theme-context';
import { PartnerGuard } from '../../guards/RolesGuard';
import TableActions from '../../shared/table/table-actions';

const TeamTable = ({
    hideSelect,
    status,
    onCloseModal,
    SuccessfullyDeleted,
}: {
    hideSelect?: boolean,
    status?: any,
    onCloseModal?: () => void,
    SuccessfullyDeleted?: () => void;
}) => {
    const dispatch = useAppDispatch();
    const { theme } = useTheme();
    const csvLink = React.useRef() as React.MutableRefObject<any>;
    const [rows, setRows] = useState<any[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>();
    const [search, setSearch] = useState<string>();
    const [sort, setSort] = useState<any>();
    const [paginationState, setPaginationState] = useState<IPaginationPayload>({
        pageNumber: 1,
        pageSize: 10,
    });
    const [submitError, setSubmitError] = useState<string>();
    const [selectedStatus, setSelectedStatus] = useState<any>();
    const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false);
    const [downloadMembers, setDownloadMembers] = useState<any>()
    const [loadingDownload, setLoadingDownload] = useState<boolean>(false)
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const paginationData = useAppSelector((state) => state.teams?.teamMembers?.data?.page);
    // const rowsLoading = useAppSelector((state) => state.teams?.membersAreLoading);
    const state = useAppSelector((state) => state.teams);

    useEffect(() => {
        if (state.teamMembers) {
            const rows =
                state.teamMembers?.data?.elements && Array.isArray(state.teamMembers?.data.elements)
                    ? state.teamMembers?.data.elements.map((r) => ({ ...r, id: r._id }))
                    : [];
            setRows(rows);
        }
    }, [state.teamMembers]);

    const onResetPaginationState = () => {
        setPaginationState({
            pageNumber: 1,
            pageSize: paginationState?.pageSize || 10,
        })
    }

    useEffect(() => {
        const settingsFilters: any = {
            ...paginationState
        }
        if (search) {
            settingsFilters.filters = search
        }
        if (selectedStatus && selectedStatus?.value !== 'all') {
            settingsFilters.data = {
                status: selectedStatus?.value
            }
        }
        if (!PartnerGuard()) {
            settingsFilters.data = {
                status: 'live'
            }
        }
        if (sort) {
            settingsFilters.data = {
                ...settingsFilters?.data || {},
                sortBy: [{ key: 'name', val: sort }]
            }
        }
        dispatch(tryToFetchTeamMembers(settingsFilters));
    }, [paginationState]);

    const onChangeSearchValue = (value: string | null | undefined, type: string) => {
        setSearch(value || undefined)
        onResetPaginationState()
    }

    const onSelectMember = (id: string) => {
        const findIfMemberExist = selectedIds && selectedIds?.length > 0 && selectedIds?.find((item: any) => item === id)
        if (findIfMemberExist) {
            const filterIds = selectedIds && selectedIds?.length > 0 && selectedIds?.filter((item: any) => item !== id)
            setSelectedIds(filterIds || [])
        } else {
            setSelectedIds((prevSelectedIds: any) =>
                [
                    ...prevSelectedIds || [],
                    id
                ]
            )
        }
    }

    const onSelectAll = () => {
        const activeMembers = rows?.filter((item) => item?.status === 'live')
        if (selectedIds && selectedIds?.length > 0) {
            setSelectedIds(undefined)
        } else {
            setSelectedIds(activeMembers?.map((item) => item?._id))
        }
    }

    const onSubmit = async (type?: string) => {
        setLoadingSubmit(true)
        try {
            onCloseModal?.()
            const data = {
                userIds: selectedIds
            }
            await dispatch(tryToDeleteTeamMembers(data)).unwrap();
            SuccessfullyDeleted?.()
        } catch (err) {
            setSubmitError(`${err}`)
        }
        setLoadingSubmit(false)
    }

    const onChangeRole = async (username: string, role: any) => {
        try {
            const data = {
                username: username,
                newRole: role
            }
            await dispatch(tryToUpdateTeamMemberRole(data)).unwrap();
        } catch (err) {
            setSubmitError(`${err}`)
        }
    }

    const formatText = (text: string | null | undefined): string => {
        if (!text) return '';
        return text
            .replace(/_/g, ' ')
            .toLowerCase()
            .split(' ')
            .map(word =>
                word.charAt(0).toUpperCase() + word.slice(1)
            )
            .join(' ');
    };

    const onClickSort = () => {
        if (sort) {
            setSort(sort === 'asc' ? 'desc' : 'asc')
        } else {
            setSort('asc')
        }
        onResetPaginationState()
    }

    const formatStatus = (status: string) => {
        if (status === 'active') {
            return <div className='flex items-center space-x-2'>
                <span className={`h-2.5 w-2.5 rounded-full bg-[#039855]`}></span>
                <p className='text-[#039855] font-normal'>Active</p>
            </div>
        }
        if (status === 'pending_invite') {
            return <div className='flex items-center space-x-2'>
                <span className={`h-2.5 w-2.5 rounded-full bg-[#535862]`}></span>
                <p className='text-[#535862] font-normal'>Pending</p>
            </div>
        }
        if (status === 'inactive') {
            return <div className='flex items-center space-x-2'>
                <span className={`h-2.5 w-2.5 rounded-full bg-[#D92D20]`}></span>
                <p className='text-[#D92D20] font-normal'>Suspended</p>
            </div>
        }
    }

    const onDownloadMembers = async () => {
        setLoadingDownload(true)
        try {
            const formatBody: any = {}
            if (search) {
                formatBody.filters = search
            }
            if (selectedStatus && selectedStatus?.value !== 'all') {
                formatBody.data = {
                    status: selectedStatus?.value
                }
            }
            if (!PartnerGuard()) {
                formatBody.data = {
                    status: 'live'
                }
            }
            if (sort) {
                formatBody.data = {
                    ...formatBody?.data || {},
                    sortBy: [{ key: 'name', val: sort }]
                }
            }
            const response: any = await dispatch(tryToFetchAllTeamMembers(formatBody)).unwrap()
            setDownloadMembers(response || [])
        } catch (err) {
            setSubmitError(`${err}`)
            setLoadingDownload(false)
        }
    }

    useEffect(() => {
        if (downloadMembers && loadingDownload) {
            csvLink.current.link.click()
        }
        setLoadingDownload(false)
    }, [downloadMembers])

    const onChangeStatus = (e: any, type?: string) => {
        setSelectedStatus(e)
        onResetPaginationState()
    }

    useEffect(() => {
        if (!selectedStatus) {
            onChangeStatus(status)
            setShowFilters(true)
        }
    }, [status])

    const onClickShowFilters = () => {
        setShowFilters(!showFilters)
    }

    const onClickDelete = async (username: string, status: string) => {
        try {
            const data = {
                data: { username: username },
                status: status
            }
            await dispatch(tryToChangeTeamMembersStatus(data)).unwrap();
        } catch (err) {
            setSubmitError(`${err}`)
        }
    }

    return (
        <div className=''>
            {submitError && <Error text={submitError} />}
            <div className='my-5 px-3 py-1 bg-[#F7F9FB] dark:bg-black flex flex-row justify-between items-center'>
                <div className='flex flex-row item-center'>
                    {/* <div className='cursor-pointer mr-2 hover:opacity-80'>
                        <img src={'/assets/navigation-icons/sidebar/add-icon.svg'} className='object-contain w-[18px]' />
                    </div> */}
                    {PartnerGuard() &&
                        <div className='cursor-pointer mr-2 hover:opacity-80' onClick={onClickShowFilters}>
                            <img src={theme === 'dark' ? '/assets/insights/filters-icon-white.svg' : '/assets/insights/filters-icon.svg'} className='object-contain w-[20px]' />
                        </div>
                    }
                    <div className='cursor-pointer mr-2 hover:opacity-80' onClick={onClickSort}>
                        <img src={theme === 'dark' ? '/assets/insights/sort-icon-white.svg' : '/assets/insights/sort-icon.svg'} className='object-contain w-[20px]' />
                    </div>
                </div>
                <div>
                    <Input
                        placeholder={'Search'}
                        containerStyle='mb-0 pb-0 relative dark:bg-bgColor-darkMode'
                        inputStyle={`mb-0 border-0 focus:outline-none dark:bg-bgColor-darkMode dark:text-primary-dark`}
                        icon={theme === 'dark' ? '/assets/navigation-icons/sidebar/search-icon-white.svg' : '/assets/shared/search.svg'}
                        showValue={true}
                        inputValue={search}
                        inputUniqueName={'search'}
                        onChangeInput={onChangeSearchValue}
                        hideInputSpace={true}
                    />
                </div>
            </div>
            {(showFilters && PartnerGuard()) &&
                <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
                    <SelectCheckbox
                        name='Member Status'
                        dataQa={'select-status'}
                        containerStyle='mb-1 min-w-full'
                        selectedOption={selectedStatus}
                        onChangeSelectedOption={(e) => onChangeStatus?.(e)}
                        options={MEMBER_STATUS || []}
                    />
                </div>
            }
            <div className='overflow-x-auto border border-1 !rounded-xl dark:border-borderColor-darkMode'>
                <table className='min-w-[800px] bg-white dark:bg-black dark:text-primary-darkMode min-h-[200px]'>

                    <thead className='bg-[#FAFAFA] dark:bg-[#3a3a3a] text-left text-sm font-medium text-black dark:text-primary-dark'>
                        <tr>
                            {(!hideSelect && PartnerGuard()) &&
                                <th className='px-4 py-3'>
                                    <input
                                        type='checkbox'
                                        checked={(selectedIds?.length === rows?.filter((item) => item?.status === 'live')?.length) && (rows?.filter((item) => item?.status === 'live')?.length !== 0)}
                                        onChange={onSelectAll}
                                        className='accent-green-600 w-4 h-4'
                                    />
                                </th>
                            }
                            <th className='px-4 py-3'>User</th>
                            <th className='px-4 py-3'>Email</th>
                            <th className='px-4 py-3'>Date</th>
                            <th className='px-4 py-3'>Status</th>
                            <th className='px-4 py-3'>Role</th>
                            <th className='px-4 py-3'>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows?.map((row) => (
                            <tr key={row.id} className='border-b hover:bg-gray-50 dark:hover:bg-bgColor-darkMode text-sm'>
                                {(!hideSelect && PartnerGuard()) &&
                                    <td className='px-4 py-4'>
                                        <input
                                            type='checkbox'
                                            disabled={row?.status !== 'live'}
                                            checked={selectedIds?.includes(row._id)}
                                            onChange={() => row?.status === 'live' && onSelectMember(row._id)}
                                            className='accent-green-600 w-4 h-4'
                                        />
                                    </td>
                                }
                                <td className='px-4 py-4 '>
                                    <div className='flex items-center'>
                                        {row?.imageUrl && <img src={row?.imageUrl || '/assets/default-avatar.png'} alt={row?.name} className='w-8 h-8 rounded-full' />}
                                        <span className='ml-3 font-medium'>{row?.name} {row?.lastName}</span>
                                    </div>
                                </td>
                                <td className='px-4 py-4'>{row?.email}</td>
                                <td className='px-4 py-4'>
                                    <div className='flex items-center text-gray-600 dark:text-primary-dark'>
                                        <img src={theme === 'dark' ? '/assets/team/calendar-icon-white.svg' : '/assets/team/calendar-icon.svg'} className='w-4 h-4 mr-1' alt='' />
                                        {moment(row.createdAt).fromNow()}
                                    </div>
                                </td>
                                <td className='px-4 py-4'>
                                    {formatStatus(row?.enabled ? 'active' : 'inactive')}
                                </td>
                                <td className='px-4 py-4'>{formatText(row?.type)}</td>
                                <td className='px-4 py-4'>
                                    <TableActions
                                        iconStyle={`rotate-90 ${row?.type === 'super_admin' ? '!cursor-not-allowed' : 'cursor-pointer'}`}
                                        containerStyle={`!mt-[-10px] !mr-[20px] !rounded-sm dark:bg-gray-800 ${row?.type === 'super_admin' ? '!cursor-not-allowed' : 'cursor-pointer'}`}>
                                        {row?.type !== 'super_admin' && <>
                                            <div
                                                onClick={() => onChangeRole(row?.username || '', row?.type === 'admins' ? 'staff' : 'adminsx')}
                                                className='bg-gray-100 dark:bg-gray-800 shadow-xl px-5 py-2 cursor-pointer hover:opacity-70'>
                                                <p className='dark:text-primary-dark'>{row?.type === 'admins' ? 'Staff' : 'Admin'}</p>
                                            </div>

                                            <div
                                                onClick={() => onClickDelete(row?.username, row?.enabled ? 'disable' : 'enable')}
                                                className='bg-gray-100 dark:bg-gray-800 shadow-xl px-5 py-2 cursor-pointer hover:opacity-70'>
                                                <p className='text-[#EF4444]'>{row?.enabled ? 'Offboard' : 'Enable'}</p>
                                            </div>
                                        </>
                                        }
                                    </TableActions>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className='flex justify-between items-center bg-[#F0F0F0] dark:bg-[#3a3a3a] text-sm text-[#8F8F8F] dark:text-primary-dark px-5 py-2 mt-1 border-t border-[#DCDCDD]'>
                    <div>
                        {(paginationData && paginationData?.totalElements > 0) && (
                            <span>
                                {1 + ((paginationData?.number - 1) * paginationData?.size)}–
                                {Math.min(paginationData?.number * paginationData?.size, paginationData?.totalElements)} of {paginationData?.totalElements} items
                            </span>
                        )}
                    </div>
                    <div className='flex gap-2 items-center'>
                        <button
                            disabled={paginationData && paginationData?.number <= 1}
                            onClick={() =>
                                setPaginationState({
                                    ...paginationState,
                                    pageNumber: paginationData ? paginationData.number - 1 : 1,
                                })
                            }
                            className='hover:bg-gray-300 dark:hover:bg-black cursor-pointer px-2 py-1 rounded disabled:opacity-50'
                        >
                            ◀
                        </button>
                        <span>
                            Page {paginationData ? paginationData.number : 0} of {paginationData ? paginationData?.totalPages : 0}
                        </span>
                        <button
                            disabled={!paginationData?.hasNextPage}
                            onClick={() =>
                                setPaginationState({
                                    ...paginationState,
                                    pageNumber: paginationData ? paginationData.number + 1 : 1,
                                })
                            }
                            className='hover:bg-gray-300 dark:hover:bg-black cursor-pointer px-2 py-1 rounded disabled:opacity-50'
                        >
                            ▶
                        </button>
                    </div>
                </div>
            </div>
            {!hideSelect &&
                <div className='flex flex-rows justify-end items-center mt-10'>
                    <Button
                        className='btn-primary !shadow-none min-w-[130px] mr-3'
                        label={loadingDownload ? 'Preparing data' : 'Download as .csv'}
                        onClickButton={() => !loadingDownload && onDownloadMembers()}
                        dataQa={'delete-role-btn'}
                    />
                    {PartnerGuard() &&
                        <Button
                            className={`${!loadingSubmit && (selectedIds && selectedIds?.length > 0) ? 'btn-main dark:bg-black dark:text-primary-dark' : 'btn-main-disable dark:bg-black opacity-80'} !shadow-none min-w-[130px] !rounded `}
                            label='Offboarding Confirm'
                            onClickButton={() => (selectedIds && selectedIds?.length > 0 && !loadingSubmit) && onSubmit()}
                            dataQa={'submit-role-btn'}
                        />
                    }
                </div>
            }
        </div>
    );
};

export default TeamTable;
